import { useState, type FormEvent, type ReactNode } from "react"
import { ArrowRight, Eye, EyeOff, KeyRound, Loader2, Mail } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import {
  getAuthErrorMessage,
  type LegacySignInResource,
  type PasswordResetCompletePayload,
  type PasswordResetStep,
  type Status,
} from "@/pages/client-area/auth-helpers"

/**
 * Primitivos de formulário compartilhados entre a página de login (chunk leve)
 * e a área do cliente (chunk carregado depois).
 */

export function Field({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <label className="grid min-w-0 gap-1.5 text-sm font-medium text-[#2d2342]">
      {label}
      {children}
    </label>
  )
}

export function Notice({
  status,
  message,
}: {
  status: Status
  message: string
}) {
  if (!message) return null

  return (
    <p
      className={cn(
        "rounded-md border px-3.5 py-2.5 text-sm",
        status === "error"
          ? "border-rose-200/70 bg-rose-50 text-rose-700"
          : "border-[#e8e0ef] bg-[#f8f6fb] text-[#433475]"
      )}
    >
      {message}
    </p>
  )
}

export function PasswordResetForm({
  email,
  emailLocked = false,
  isLoaded,
  onCancel,
  onComplete,
  onEmailChange,
  signIn,
}: {
  email: string
  emailLocked?: boolean
  isLoaded: boolean
  onCancel: () => void
  onComplete: (payload: PasswordResetCompletePayload) => Promise<void> | void
  onEmailChange: (email: string) => void
  signIn: LegacySignInResource | undefined
}) {
  const [step, setStep] = useState<PasswordResetStep>("request")
  const [targetEmail, setTargetEmail] = useState("")
  const [code, setCode] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [status, setStatus] = useState<Status>("idle")
  const [message, setMessage] = useState("")
  const isBusy = status === "saving"
  const canUseClerk = isLoaded && Boolean(signIn)

  async function sendResetCode(identifier: string) {
    if (!canUseClerk || isBusy || !signIn) return
    const normalizedEmail = identifier.trim().toLowerCase()
    if (!normalizedEmail) {
      setStatus("error")
      setMessage("Informe o e-mail cadastrado para receber o código.")
      return
    }

    setStatus("saving")
    setMessage("")
    try {
      await signIn.create({
        strategy: "reset_password_email_code",
        identifier: normalizedEmail,
      })
      setTargetEmail(normalizedEmail)
      setStep("verify")
      setStatus("success")
      setMessage(`Enviamos um código de recuperação para ${normalizedEmail}.`)
    } catch (error) {
      setStatus("error")
      setMessage(getAuthErrorMessage(error, "Não foi possível enviar o código."))
    }
  }

  async function handleSendCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    await sendResetCode(email)
  }

  async function handleVerifyCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canUseClerk || isBusy || !signIn) return
    const cleanCode = code.trim()
    if (!cleanCode) {
      setStatus("error")
      setMessage("Informe o código recebido por e-mail.")
      return
    }

    setStatus("saving")
    setMessage("")
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: cleanCode,
      })
      if (result.status !== "needs_new_password") {
        setStatus("error")
        setMessage(
          `Complete as etapas pendentes para alterar a senha. Status: ${result.status ?? "indefinido"}.`
        )
        return
      }
      setStep("password")
      setStatus("success")
      setMessage("Código confirmado. Defina uma nova senha.")
    } catch (error) {
      setStatus("error")
      setMessage(getAuthErrorMessage(error, "Não foi possível validar o código."))
    }
  }

  async function handleUpdatePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canUseClerk || isBusy || !signIn) return
    const password = newPassword.trim()
    const confirmation = confirmPassword.trim()
    if (password.length < 8) {
      setStatus("error")
      setMessage("A nova senha deve ter pelo menos 8 caracteres.")
      return
    }
    if (password !== confirmation) {
      setStatus("error")
      setMessage("A confirmação precisa ser igual à nova senha.")
      return
    }

    setStatus("saving")
    setMessage("")
    try {
      const result = await signIn.resetPassword({
        password,
        signOutOfOtherSessions: true,
      })
      if (result.status !== "complete") {
        setStatus("error")
        setMessage(
          `Complete as etapas pendentes para alterar a senha. Status: ${result.status ?? "indefinido"}.`
        )
        return
      }
      setStatus("success")
      setMessage("Senha alterada com sucesso.")
      try {
        await onComplete({
          createdSessionId: result.createdSessionId,
          email: targetEmail || email.trim().toLowerCase(),
        })
      } catch (error) {
        setStatus("error")
        setMessage(
          getAuthErrorMessage(
            error,
            "Senha alterada, mas não foi possível iniciar a sessão automaticamente. Volte e entre com a nova senha."
          )
        )
      }
    } catch (error) {
      setStatus("error")
      setMessage(getAuthErrorMessage(error, "Não foi possível alterar a senha."))
    }
  }

  if (step === "verify") {
    return (
      <form onSubmit={handleVerifyCode} className="grid min-w-0 gap-4">
        <Field label="Código recebido por e-mail">
          <Input
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className="h-11 max-w-full"
          />
        </Field>
        <Notice status={status} message={message} />
        <div className="grid gap-2 sm:grid-cols-2">
          <Button
            type="button"
            variant="outline"
            disabled={isBusy || !canUseClerk}
            onClick={() => void sendResetCode(targetEmail || email)}
            className="h-11 max-w-full"
          >
            <Mail />
            Reenviar código
          </Button>
          <Button
            type="submit"
            disabled={isBusy || !canUseClerk}
            className="h-11 max-w-full bg-[#e82b77] text-white hover:bg-[#cf1f66]"
          >
            {isBusy ? <Loader2 className="animate-spin" /> : <ArrowRight />}
            Validar código
          </Button>
        </div>
        <Button type="button" variant="ghost" onClick={onCancel} className="h-10">
          Voltar
        </Button>
      </form>
    )
  }

  if (step === "password") {
    return (
      <form onSubmit={handleUpdatePassword} className="grid min-w-0 gap-4">
        <Field label="Nova senha">
          <div className="relative min-w-0">
            <Input
              required
              minLength={8}
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className="h-11 max-w-full pr-11"
              autoComplete="new-password"
            />
            <button
              type="button"
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              aria-pressed={showPassword}
              onClick={() => setShowPassword((current) => !current)}
              className="absolute right-2 top-1/2 grid size-8 -translate-y-1/2 place-items-center rounded-md text-[#6b6080] transition hover:bg-[#f3eef8] hover:text-[#201936] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e82b77]/30"
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </Field>
        <Field label="Confirmar nova senha">
          <Input
            required
            minLength={8}
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            className="h-11 max-w-full"
            autoComplete="new-password"
          />
        </Field>
        <Notice status={status} message={message} />
        <Button
          type="submit"
          disabled={isBusy || !canUseClerk}
          className="h-11 max-w-full bg-[#e82b77] text-white hover:bg-[#cf1f66]"
        >
          {isBusy ? <Loader2 className="animate-spin" /> : <KeyRound />}
          Alterar senha
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} className="h-10">
          Voltar
        </Button>
      </form>
    )
  }

  return (
    <form onSubmit={handleSendCode} className="grid min-w-0 gap-4">
      <Field label="E-mail cadastrado">
        <Input
          required
          type="email"
          value={email}
          onChange={(event) => onEmailChange(event.target.value)}
          disabled={emailLocked}
          className="h-11 max-w-full"
          autoComplete="email"
        />
      </Field>
      <Notice status={status} message={message} />
      <Button
        type="submit"
        disabled={isBusy || !canUseClerk}
        className="h-11 max-w-full bg-[#e82b77] text-white hover:bg-[#cf1f66]"
      >
        {isBusy ? <Loader2 className="animate-spin" /> : <Mail />}
        Enviar código
      </Button>
      <Button type="button" variant="ghost" onClick={onCancel} className="h-10">
        Voltar
      </Button>
    </form>
  )
}
